/**
 * Typed API layer — a thin, fully-typed wrapper over `apiRequest` (issue #637).
 *
 * The URL, its path/query parameters, the request body, and the response type
 * are all derived from the generated OpenAPI `paths` (`generated/backend.d.ts`),
 * so a wrong route (including a stray trailing slash), a wrong query-param name,
 * or a mismatched body/response **fails `tsc`** rather than shipping silently.
 * These were the root cause of #607, #631, and #634.
 *
 * It deliberately delegates to `apiRequest`, leaving the auth-header injection,
 * single-flight 401 refresh, cross-tab locking, and error-toast interceptor
 * (all in `client.ts`, hardened by #639) completely untouched — this adds
 * compile-time typing only, no runtime HTTP behaviour of its own.
 */
import { apiRequest } from "./client";
import type { paths } from "./generated/backend";

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/** Passthrough config; `suppressErrorToast` mirrors `apiRequest`'s option. */
export interface TypedRequestConfig {
  suppressErrorToast?: boolean;
}

// ─── Type extraction from the generated `paths` ─────────────────────────────

/** Paths that declare the given method. */
type PathsFor<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends Record<M, unknown> ? P : never;
}[keyof paths];

/** The operation object for a path + method. */
type Op<P extends keyof paths, M extends HttpMethod> =
  paths[P] extends Record<M, infer O> ? O : never;

/** 2xx JSON response body (204 → void; absent → unknown). */
type ResponseOf<O> = O extends { responses: infer R }
  ? R extends { 200: { content: { "application/json": infer T } } }
    ? T
    : R extends { 201: { content: { "application/json": infer T } } }
      ? T
      : R extends { 204: unknown }
        ? void
        : unknown
  : unknown;

type PathParamsOf<O> = O extends { parameters: { path: infer P } } ? P : never;
type QueryParamsOf<O> = O extends { parameters: { query?: infer Q } }
  ? Q extends undefined
    ? never
    : NonNullable<Q>
  : never;
type BodyOf<O> = O extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : never;

/**
 * Options shape for an operation — `path` and `body` are required when the
 * operation has them, `query` stays optional. Empty when the op needs nothing.
 */
type Init<O> = ([PathParamsOf<O>] extends [never] ? object : { path: PathParamsOf<O> }) &
  ([QueryParamsOf<O>] extends [never] ? object : { query?: QueryParamsOf<O> }) &
  ([BodyOf<O>] extends [never] ? object : { body: BodyOf<O> }) & { config?: TypedRequestConfig };

type RequiredKeys<T> = { [K in keyof T]-?: object extends Pick<T, K> ? never : K }[keyof T];

/** Options arg is optional only when the operation requires nothing. */
type Args<O> = [RequiredKeys<Init<O>>] extends [never] ? [init?: Init<O>] : [init: Init<O>];

// ─── Runtime helpers ────────────────────────────────────────────────────────

/** Interpolate `{name}` placeholders and append a query string. */
const buildUrl = (
  template: string,
  path?: Record<string, unknown>,
  query?: Record<string, unknown>
): string => {
  let url = template;
  if (path) {
    for (const [key, value] of Object.entries(path)) {
      url = url.replace(`{${key}}`, encodeURIComponent(String(value)));
    }
  }
  if (query) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      // Skip absent values so an optional filter isn't sent as "undefined".
      if (value === undefined || value === null) {
        continue;
      }
      // FastAPI reads a list parameter from the key repeated
      // (`languages=sv&languages=en`); `String(["sv", "en"])` would send
      // "sv,en" as one value (#1140).
      if (Array.isArray(value)) {
        for (const item of value) {
          qs.append(key, String(item));
        }
        continue;
      }
      qs.append(key, String(value));
    }
    const s = qs.toString();
    if (s) {
      url += `?${s}`;
    }
  }
  return url;
};

const pick = (init: unknown) =>
  (init ?? {}) as {
    path?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
    config?: TypedRequestConfig;
  };

// ─── The typed client ───────────────────────────────────────────────────────

export const api = {
  get: <P extends PathsFor<"get">>(path: P, ...args: Args<Op<P, "get">>) => {
    const { path: p, query, config } = pick(args[0]);
    return apiRequest.get<ResponseOf<Op<P, "get">>>(buildUrl(path, p, query), config);
  },

  post: <P extends PathsFor<"post">>(path: P, ...args: Args<Op<P, "post">>) => {
    const { path: p, query, body, config } = pick(args[0]);
    return apiRequest.post<ResponseOf<Op<P, "post">>>(buildUrl(path, p, query), body, config);
  },

  put: <P extends PathsFor<"put">>(path: P, ...args: Args<Op<P, "put">>) => {
    const { path: p, query, body } = pick(args[0]);
    return apiRequest.put<ResponseOf<Op<P, "put">>>(buildUrl(path, p, query), body);
  },

  patch: <P extends PathsFor<"patch">>(path: P, ...args: Args<Op<P, "patch">>) => {
    const { path: p, query, body } = pick(args[0]);
    return apiRequest.patch<ResponseOf<Op<P, "patch">>>(buildUrl(path, p, query), body);
  },

  delete: <P extends PathsFor<"delete">>(path: P, ...args: Args<Op<P, "delete">>) => {
    const { path: p, query, body } = pick(args[0]);
    return apiRequest.delete<ResponseOf<Op<P, "delete">>>(buildUrl(path, p, query), {
      data: body,
    });
  },
};
